import React, { useState } from 'react';
import { ExperienceLevel } from '@skyment-org/shared-lib';

export interface MentorOnboardingData {
  experienceLevel: ExperienceLevel;
  yearsOfExperience: number;
  currentJobTitle: string;
  currentCompany: string;
  skills: string[];
  industries: string[];
  maxMentees: number;
  isAvailableForPaidSessions: boolean;
  hourlyRate?: number;
  bio: string;
  location: string;
  languages: string[];
  mentorshipApproach: string;
}

interface MentorOnboardingFormProps {
  onSubmit: (data: MentorOnboardingData) => void;
  onBack: () => void;
  isLoading?: boolean;
}

const MentorOnboardingForm: React.FC<MentorOnboardingFormProps> = ({
  onSubmit,
  onBack,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<MentorOnboardingData>({
    experienceLevel: ExperienceLevel.MID,
    yearsOfExperience: 0,
    currentJobTitle: '',
    currentCompany: '',
    skills: [],
    industries: [],
    maxMentees: 5,
    isAvailableForPaidSessions: false,
    bio: '',
    location: '',
    languages: ['English'],
    mentorshipApproach: ''
  });

  const [skillInput, setSkillInput] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddSkill = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && skillInput.trim()) {
      e.preventDefault();
      if (!formData.skills.includes(skillInput.trim())) {
        setFormData(prev => ({
          ...prev,
          skills: [...prev.skills, skillInput.trim()]
        }));
      }
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleAddIndustry = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && industryInput.trim()) {
      e.preventDefault();
      if (!formData.industries.includes(industryInput.trim())) {
        setFormData(prev => ({
          ...prev,
          industries: [...prev.industries, industryInput.trim()]
        }));
      }
      setIndustryInput('');
    }
  };

  const handleRemoveIndustry = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industries: prev.industries.filter(i => i !== industry)
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentJobTitle.trim()) {
      newErrors.currentJobTitle = 'Job title is required';
    }

    if (!formData.currentCompany.trim()) {
      newErrors.currentCompany = 'Company is required';
    }

    if (formData.yearsOfExperience < 0) {
      newErrors.yearsOfExperience = 'Years of experience must be positive';
    }

    if (formData.skills.length === 0) {
      newErrors.skills = 'At least one skill is required';
    }

    if (formData.industries.length === 0) {
      newErrors.industries = 'At least one industry is required';
    }

    if (formData.maxMentees < 1 || formData.maxMentees > 20) {
      newErrors.maxMentees = 'Max mentees must be between 1 and 20';
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Mentor Profile</h1>
        <p className="text-gray-600">
          Tell us about yourself so we can match you with the right mentees.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Experience Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Experience Level *
          </label>
          <select
            name="experienceLevel"
            value={formData.experienceLevel}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {Object.values(ExperienceLevel).map(level => (
              <option key={level} value={level}>
                {level.charAt(0) + level.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Years of Experience */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Years of Experience *
          </label>
          <input
            type="number"
            name="yearsOfExperience"
            value={formData.yearsOfExperience}
            onChange={handleInputChange}
            min="0"
            max="50"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.yearsOfExperience ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.yearsOfExperience && (
            <p className="mt-1 text-sm text-red-600">{errors.yearsOfExperience}</p>
          )}
        </div>

        {/* Current Job Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Job Title *
          </label>
          <input
            type="text"
            name="currentJobTitle"
            value={formData.currentJobTitle}
            onChange={handleInputChange}
            placeholder="e.g., Senior Software Engineer"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.currentJobTitle ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.currentJobTitle && (
            <p className="mt-1 text-sm text-red-600">{errors.currentJobTitle}</p>
          )}
        </div>

        {/* Current Company */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Company *
          </label>
          <input
            type="text"
            name="currentCompany"
            value={formData.currentCompany}
            onChange={handleInputChange}
            placeholder="e.g., Tech Corp"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.currentCompany ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.currentCompany && (
            <p className="mt-1 text-sm text-red-600">{errors.currentCompany}</p>
          )}
        </div>

        {/* Skills */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Skills * (Press Enter to add)
          </label>
          <input
            type="text"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={handleAddSkill}
            placeholder="e.g., JavaScript, React, Node.js"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.skills ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.skills.map(skill => (
              <span
                key={skill}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => handleRemoveSkill(skill)}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          {errors.skills && <p className="mt-1 text-sm text-red-600">{errors.skills}</p>}
        </div>

        {/* Industries */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industries * (Press Enter to add)
          </label>
          <input
            type="text"
            value={industryInput}
            onChange={(e) => setIndustryInput(e.target.value)}
            onKeyDown={handleAddIndustry}
            placeholder="e.g., Technology, Finance, Healthcare"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.industries ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.industries.map(industry => (
              <span
                key={industry}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
              >
                {industry}
                <button
                  type="button"
                  onClick={() => handleRemoveIndustry(industry)}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          {errors.industries && <p className="mt-1 text-sm text-red-600">{errors.industries}</p>}
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location *
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder="e.g., San Francisco, CA"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.location ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
        </div>

        {/* Max Mentees */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Maximum Number of Mentees *
          </label>
          <input
            type="number"
            name="maxMentees"
            value={formData.maxMentees}
            onChange={handleInputChange}
            min="1"
            max="20"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.maxMentees ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.maxMentees && <p className="mt-1 text-sm text-red-600">{errors.maxMentees}</p>}
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bio * (Tell mentees about yourself)
          </label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            rows={4}
            placeholder="Share your background, expertise, and what you're passionate about mentoring..."
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.bio ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.bio && <p className="mt-1 text-sm text-red-600">{errors.bio}</p>}
        </div>

        {/* Mentorship Approach */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mentorship Approach (Optional)
          </label>
          <textarea
            name="mentorshipApproach"
            value={formData.mentorshipApproach}
            onChange={handleInputChange}
            rows={3}
            placeholder="Describe your mentoring style and approach..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-primary-500 text-white rounded-lg font-bold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Completing...' : 'Complete Profile'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MentorOnboardingForm;
